import api from '../helpers/axios';

// ── Summary / Coverage ────────────────────────────────────────────────────

export interface IStateSummary {
  stateId: number;
  stateName: string;
  stateCode: string;
  gstCode: string;
  articleCount: number;
  reporterCount: number;
  userCount: number;
  hasCoverage: boolean;
}

export interface ICoverageSummary {
  periodDays: number;
  fromDate: string;
  toDate: string;
  totalStates: number;
  coveredStates: number;
  uncoveredStates: number;
  coveragePercent: number;
  states: IStateSummary[];
}

export interface ICoverageGap {
  stateId: number;
  stateName: string;
  stateCode: string;
}

export interface ICoverageGapsResponse {
  periodDays: number;
  fromDate: string;
  toDate: string;
  gapCount: number;
  gaps: ICoverageGap[];
}

export interface ITrendingState {
  stateId: number;
  stateName: string;
  stateCode: string;
  articleCount: number;
}

export interface IStateTrendingResponse {
  periodDays: number;
  fromDate: string;
  toDate: string;
  states: ITrendingState[];
}

// ── Author attribution ─────────────────────────────────────────────────────

export type TAuthorType = 'ADMIN' | 'EDITOR' | 'PUBLISHER' | 'REPORTER' | 'AUTHOR' | 'STAFF' | 'UNKNOWN';

export interface IArticleAuthor {
  authorId: string | null;
  authorName: string | null;
  authorType: TAuthorType;
  authorProfileImageUrl: string | null;
}

// ── Article drilldown ──────────────────────────────────────────────────────

export interface IStateArticle {
  articleId: string;
  title: string;
  description: string | null;
  thumbnailPath: string | null;
  publishedStatus: number;
  createdAt: string;
  publishedAt: string | null;
  tags: string | null;
  postType: string | null;
  isAdvertisement: boolean;
  contentLanguageCode: string | null;
  targetScopeCode: string | null;
  verificationCode: string | null;
  subArticleCount: number;
  author: IArticleAuthor;
}

export interface IStateArticlesResponse {
  stateId: number;
  periodDays: number;
  fromDate: string;
  toDate: string;
  total: number;
  page: number;
  limit: number;
  count: number;
  totalPages: number;
  articles: IStateArticle[];
}

export interface IArticlesFilter {
  days?: number;
  fromDate?: string;   // ISO date string: "YYYY-MM-DD"
  toDate?: string;
  publishedStatus?: number;
  page?: number;
  limit?: number;
}

// ── State assignment ───────────────────────────────────────────────────────

export interface IStateAssignment {
  assignmentId: number;
  userId: string;
  stateId: number;
  assignedBy: string;
  assignedAt: string;
  isActive: boolean;
  notes: string;
}

// ── Service ────────────────────────────────────────────────────────────────

class StateCoverageService {
  private readonly coverageBase = '/api/admin/state-coverage';
  private readonly assignmentBase = '/api/admin/state-assignments';

  async getCoverageSummary(days = 7, fromDate?: string, toDate?: string): Promise<ICoverageSummary> {
    const res = await api.get(`${this.coverageBase}/summary`, {
      params: { days, ...(fromDate && { fromDate }), ...(toDate && { toDate }) },
    });
    return res.data;
  }

  async getCoverageGaps(days = 7, fromDate?: string, toDate?: string): Promise<ICoverageGapsResponse> {
    const res = await api.get(`${this.coverageBase}/gaps`, {
      params: { days, ...(fromDate && { fromDate }), ...(toDate && { toDate }) },
    });
    return res.data;
  }

  async getTrendingByState(days = 7, fromDate?: string, toDate?: string): Promise<IStateTrendingResponse> {
    const res = await api.get(`${this.coverageBase}/trending`, {
      params: { days, ...(fromDate && { fromDate }), ...(toDate && { toDate }) },
    });
    return res.data;
  }

  async getReporterDistribution(): Promise<{ reportersByState: any[] }> {
    const res = await api.get(`${this.coverageBase}/reporters`);
    return res.data;
  }

  async getUserDistribution(): Promise<{ usersByState: any[] }> {
    const res = await api.get(`${this.coverageBase}/users`);
    return res.data;
  }

  /**
   * Drilldown: fetch paginated articles for a specific state with author attribution.
   * Supports date range, published status, and pagination filters.
   */
  async getArticlesByState(stateId: number, filters: IArticlesFilter = {}): Promise<IStateArticlesResponse> {
    const { days = 30, fromDate, toDate, publishedStatus = 1, page = 0, limit = 20 } = filters;
    const res = await api.get(`${this.coverageBase}/articles`, {
      params: {
        stateId,
        days,
        publishedStatus,
        page,
        limit,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
      },
    });
    return res.data;
  }

  // ── State editor assignment APIs ──────────────────────────────────────────

  async getAssignmentsForUser(userId: string): Promise<{ userId: string; assignments: IStateAssignment[] }> {
    const res = await api.get(`${this.assignmentBase}/user/${userId}`);
    return res.data;
  }

  async assignUserToState(payload: {
    userId: string;
    stateId: number;
    assignedBy: string;
    notes?: string;
  }): Promise<{ success: boolean; assignment: IStateAssignment }> {
    const res = await api.post(this.assignmentBase, payload);
    return res.data;
  }

  async removeAssignment(userId: string, stateId: number): Promise<{ success: boolean }> {
    const res = await api.delete(`${this.assignmentBase}/user/${userId}/state/${stateId}`);
    return res.data;
  }

  async removeAllAssignments(userId: string): Promise<{ success: boolean; removedCount: number }> {
    const res = await api.delete(`${this.assignmentBase}/user/${userId}`);
    return res.data;
  }
}

export const stateCoverageService = new StateCoverageService();
export default stateCoverageService;
